import React, { useCallback, useEffect, useState, useMemo } from 'react';
import SlidingSideBar from '../../../SlidingSideBar/SlidingSideBar';
import { Col, FormControl, Row, Card } from 'react-bootstrap';
import isEqual from 'lodash/isEqual';
import { ENDPOINTS, ORDER_TYPES, BACKEND_FIELDS_TO_EXCLUDE } from '../../../../helpers/constants';
import { makeRESTApiRequest } from '../../../../helpers/api';
import Loader from '../../../Loader';
import { toast } from 'sonner';
import useReportGeneration from '../../../../hooks/useReportGeneration';
import ReportFormGenerator from '../../../reports/FormGenerator';
import StepTabNavigation from '../../../reports/StepTabNavigation';
import ReportActionBar from '../../../reports/ReportActionBar';
import ReportModals from '../../../reports/ReportModals';
import ReportSidebarFooter from '../../../reports/ReportSidebarFooter';
import {
  autoFillInvoicesFromFiles,
  buildFieldStatusMapFromQaIssues,
  buildQaFieldIssueMap,
  buildUnresolvedQaIssueStepKeys,
  computeSuggestedInvoiceAmount,
  createEmptySections,
  EMPTY_INVOICE_ROW,
  fetchReportQaSectionsByOrder,
  getInvoiceMultiFields,
  handleInvoiceMultiFieldChange,
  isInvoiceTotalBelowSuggested,
  resolveQaIssueNavigation,
  resolveReportOrderId,
  scrollToFieldKey
} from '../../../reports/utils/helpers';
import { FieldStatusShell } from '../../../common/fieldStatus';
import QaAnalysisSideBar from '../../../reports/QaAnalysisSideBar';
import useQuery from '../../../../hooks/useQueryParams';
import { Plus, X } from 'lucide-react';
import EstoppelReportHoaSummaryPanel from './EstoppelReportHoaSummaryPanel';
import AddEditOrderSidebar from '../AddEditOrderSidebar';
import useAppChoices from '../../../../hooks/useAppChoices';
import { getFormattedDate, isClient, normalizeRichTextValue } from '../../../../helpers/global';
import FolioInputWithCopy from '../../../common/FolioInputWithCopy';
import { appendHoaSlotFiles, hasPendingHoaFiles } from '../../../hoaForm/hoaFormData';

const toClosingDateInputValue = value => {
  if (!value) return '';
  if (typeof value === 'string') {
    const datePart = value.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return getFormattedDate(date);
};

const YES_NO_OPTIONS = ['No', 'Yes'];
const PAID_THROUGH_DATE_OPTION = 'Date';
const PAID_THROUGH_TEXT_OPTION = 'Text';
// react-select treats option groups via `'options' in option`; string options like "Date" throw.
const PAID_THROUGH_CHOICE_OPTIONS = [PAID_THROUGH_DATE_OPTION, PAID_THROUGH_TEXT_OPTION].map(
  value => ({ label: value, value })
);

const toYesNoValue = (value) =>
  value === 'Yes' || value === 'No' ? value : undefined;

const deriveHoaSlotCountFromEstoppel = estoppel => {
  const fromReport = estoppel?.report?.hoas?.length || 0;
  const fromOrderOnReport = estoppel?.report?.orderId?.homeOwnerInformation?.length || 0;
  return Math.min(3, Math.max(1, Math.max(fromReport, fromOrderOnReport, 1)));
};

/** Per visible HOA tab: backend ids for save/merge (empty object = new tab, no _id/orderHoaId). */
const buildHoaSlotRefsFromReport = (report, slotCount) => {
  const hoas = report?.hoas || [];
  const refs = [];
  for (let i = 0; i < slotCount; i++) {
    const h = hoas[i];
    if (!h) {
      refs.push({});
      continue;
    }
    refs.push({
      ...(h._id != null && { _id: String(h._id) }),
      ...(h.orderHoaId != null && { orderHoaId: String(h.orderHoaId) })
    });
  }
  return refs;
};

const getHomeOwnerInformationFromEstoppel = estoppel => {
  const fromOrderOnReport = estoppel?.report?.orderId?.homeOwnerInformation;
  if (Array.isArray(fromOrderOnReport) && fromOrderOnReport.length) return fromOrderOnReport;
  return [];
};

const resolveOrderHoaDisplaySlots = (estoppel, service, estoppelOrderId, override) => {
  if (Array.isArray(override) && override.length) return override;
  const fromService = service?.orders?.find(
    o => o?._id && String(o._id) === String(estoppelOrderId)
  )?.homeOwnerInformation;
  if (Array.isArray(fromService) && fromService.length) return fromService;
  return getHomeOwnerInformationFromEstoppel(estoppel);
};

const padHoaDisplaySlots = (sourceList, slotCount) =>
  Array.from({ length: Math.max(0, slotCount) }, (_, hoaIndex) => {
    const row = (Array.isArray(sourceList) && sourceList[hoaIndex]) || {};
    return { ...row };
  });

const normalizeHoaExplanationSnapshot = (rows, slotCount) =>
  Array.from({ length: Math.max(0, slotCount) }, (_, i) =>
    String((Array.isArray(rows) ? rows[i] : null)?.noEstoppelNeededExplanation ?? '').trim()
  );

const buildExplanationsFromSlots = (slots, slotCount) =>
  Array.from({ length: Math.max(0, slotCount) }, (_, i) => {
    const row = (Array.isArray(slots) && slots[i]) || {};
    return row.noEstoppelNeededExplanation != null
      ? String(row.noEstoppelNeededExplanation)
      : '';
  });

/** Directory snapshot fields sent on report.hoas for sync on save/preview (not stored on report). */
const HOA_DIRECTORY_SYNC_KEYS = [
  '_id',
  'associationId',
  'name',
  'phoneNumber',
  'email',
  'associationPhoneNumber',
  'associationEmail',
  'managementType',
  'managementCompany',
  'managementCompanyId',
  'companyPhone',
  'companyEmail',
  'managementCompanyContacts',
  'associationContacts',
  'orderingInstructions',
  'noEstoppelNeeded',
  'noEstoppelNeededExplanation',
  '_removedAssociationFileIds',
  '_removedManagementCompanyFileIds'
];

const hoaDirectoryFieldsForOrderSyncPayload = row => {
  if (!row || typeof row !== 'object') return {};
  const out = {};
  for (const key of HOA_DIRECTORY_SYNC_KEYS) {
    if (!(key in row)) continue;
    out[key] = row[key];
  }
  return out;
};

const normalizeHoaDirectorySnapshot = (rows, slotCount) =>
  Array.from({ length: Math.max(0, slotCount) }, (_, i) => {
    const row = (Array.isArray(rows) && rows[i]) || {};
    return {
      associationId: row.associationId != null ? String(row.associationId) : '',
      name: String(row.name ?? '').trim(),
      phoneNumber: String(row.phoneNumber ?? '').trim(),
      email: String(row.email ?? '').trim().toLowerCase(),
      associationPhoneNumber: String(row.associationPhoneNumber ?? '').trim(),
      associationEmail: String(row.associationEmail ?? '').trim().toLowerCase(),
      ...(row.managementType === 'self' || row.managementType === 'managed'
        ? { managementType: row.managementType }
        : {}),
      managementCompany: String(row.managementCompany ?? '').trim(),
      managementCompanyId:
        row.managementCompanyId != null ? String(row.managementCompanyId) : '',
      companyPhone: String(row.companyPhone ?? '').trim(),
      companyEmail: String(row.companyEmail ?? '').trim().toLowerCase(),
      orderingInstructions: String(row.orderingInstructions ?? ''),
      noEstoppelNeeded: !!row.noEstoppelNeeded,
      noEstoppelNeededExplanation: String(row.noEstoppelNeededExplanation ?? '').trim()
    };
  });

/** Slots and explanation snapshot for dirty-check / refs after save. */
const buildHoaUiState = (estoppel, service, estoppelOrderId, { savedReport, override } = {}) => {
  const estoppelForSlots = savedReport ? { ...estoppel, report: savedReport } : estoppel;
  const slots = deriveHoaSlotCountFromEstoppel(estoppelForSlots);
  const source = override
    || (savedReport?.orderId?.homeOwnerInformation?.length
      ? savedReport.orderId.homeOwnerInformation
      : null)
    || resolveOrderHoaDisplaySlots(estoppelForSlots, service, estoppelOrderId, null);
  const rows = padHoaDisplaySlots(source, slots);
  return {
    slots,
    rows,
    hoaExplanations: normalizeHoaExplanationSnapshot(rows, slots),
    hoaDirectory: normalizeHoaDirectorySnapshot(rows, slots),
    reportForRefs: savedReport ?? estoppel?.report
  };
};

const getHoaNameAtIndex = (homeOwnerRows, index) => {
  const row = Array.isArray(homeOwnerRows) ? homeOwnerRows[index] : null;
  if (row?.name == null) return '';
  return String(row.name).trim();
};

const formatHoaTabName = (hoaNum, hoaName) => {
  const trimmed = String(hoaName ?? '').trim();
  return trimmed ? `HOA ${hoaNum} — ${trimmed}` : `HOA ${hoaNum}`;
};

const formatHoaDocumentsLabel = (hoaNum, hoaName) => {
  const trimmed = String(hoaName ?? '').trim();
  const base = `HOA ${hoaNum} Documents`;
  return trimmed ? `${base} — ${trimmed}` : base;
};

const HOA_SECTION_KEYS = ['hoa1', 'hoa2', 'hoa3'];
const HOA_FILE_KEYS = ['hoa1Files', 'hoa2Files', 'hoa3Files'];

const reorderListByIndex = (list, fromIndex, toIndex) => {
  const next = [...list];
  const [removed] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, removed);
  return next;
};

const getHoaSlotIndexAfterReorder = (slotCount, fromIndex, toIndex, currentSlotIndex) => {
  const permuted = reorderListByIndex(
    Array.from({ length: slotCount }, (_, i) => i),
    fromIndex,
    toIndex
  );
  return permuted.indexOf(currentSlotIndex);
};

const getFormSteps = (isLayoutConstrained, hoaCount = 0, associationDuesTypeOptions = [], homeOwnerRows = []) => {
  const hoaNameAt = index => getHoaNameAtIndex(homeOwnerRows, index);
  const steps = [
    {
      key: 'documents',
      name: 'Documents',
      fields: [
        {
          label: 'Invoice Documents',
          key: 'invoicesFiles',
          type: 'file',
          cellSize: isLayoutConstrained ? 12 : 6,
          isInDocumentsTrue: true
        },
        ...(hoaCount >= 1
          ? [
              {
                label: formatHoaDocumentsLabel(1, hoaNameAt(0)),
                key: 'hoa1Files',
                type: 'file',
                cellSize: isLayoutConstrained ? 12 : 6,
                isInDocumentsTrue: true
              }
            ]
          : []),
        ...(hoaCount >= 2
          ? [
              {
                label: formatHoaDocumentsLabel(2, hoaNameAt(1)),
                key: 'hoa2Files',
                type: 'file',
                cellSize: isLayoutConstrained ? 12 : 6,
                isInDocumentsTrue: true
              }
            ]
          : []),
        ...(hoaCount >= 3
          ? [
              {
                label: formatHoaDocumentsLabel(3, hoaNameAt(2)),
                key: 'hoa3Files',
                type: 'file',
                cellSize: isLayoutConstrained ? 12 : 6,
                isInDocumentsTrue: true
              }
            ]
          : [])
      ]
    }
  ];

  const hoaColsDense = isLayoutConstrained ? { cellSize: 4 } : { cellSizeMd: 4, cellSizeLg: 3 };

  const hoaFields = [
    {
      label: 'Association Dues Amount',
      key: 'associationDuesDueAmount',
      type: 'currency-input',
      ...hoaColsDense,
      groupKey: 'hoa'
    },
    {
      label: 'Association Dues Type',
      key: 'associationDuesType',
      type: 'select',
      options: associationDuesTypeOptions,
      ...hoaColsDense,
      groupKey: 'hoa',
      menuLabel: 'Select an association dues type'
    },
    {
      label: 'Paid Through',
      key: 'paidThroughChoice',
      type: 'select',
      options: PAID_THROUGH_CHOICE_OPTIONS,
      ...hoaColsDense,
      groupKey: 'hoa',
      menuLabel: 'Select paid through'
    },
    {
      label: 'Paid Through Date',
      key: 'paidThrough',
      type: 'date',
      ...hoaColsDense,
      show: {
        key: 'paidThroughChoice',
        value: PAID_THROUGH_DATE_OPTION
      },
      groupKey: 'hoa'
    },
    {
      label: 'Paid Through Text',
      key: 'paidThroughText',
      type: 'text',
      ...hoaColsDense,
      show: {
        key: 'paidThroughChoice',
        value: PAID_THROUGH_TEXT_OPTION
      },
      groupKey: 'hoa'
    },
    {
      label: 'Collections',
      key: 'collections',
      type: 'radio',
      options: YES_NO_OPTIONS,
      ...hoaColsDense,
      groupKey: 'hoa'
    },
    {
      label: 'Buyer Approval Required',
      key: 'buyerApprovalRequired',
      type: 'radio',
      options: YES_NO_OPTIONS,
      ...hoaColsDense,
      groupKey: 'hoa'
    },
    {
      label: 'Violations',
      key: 'violations',
      type: 'radio',
      options: YES_NO_OPTIONS,
      cellSize: isLayoutConstrained ? 4 : 3,
      groupKey: 'hoa'
    },
    {
      label: 'Open Litigation',
      key: 'openLitigation',
      type: 'radio',
      options: YES_NO_OPTIONS,
      ...hoaColsDense,
      groupKey: 'hoa'
    },
    {
      type: 'offset',
      cellSize: 12,
      style: { minHeight: 0 },
      groupKey: 'hoa'
    },
    {
      label: 'Special Assessments',
      key: 'specialAssessmentsHasSpecialAssessments',
      type: 'radio',
      options: YES_NO_OPTIONS,
      ...hoaColsDense,
      groupKey: 'hoa'
    },
    {
      type: 'offset',
      cellSize: isLayoutConstrained ? 4 : 6,
      style: { minHeight: '79px' },
      showWhenNot: {
        key: 'specialAssessmentsHasSpecialAssessments',
        value: 'Yes'
      },
      groupKey: 'hoa'
    },
    {
      label: 'Special Assessments Due Amount',
      key: 'specialAssessmentsDueAmount',
      type: 'currency-input',
      ...hoaColsDense,
      show: {
        key: 'specialAssessmentsHasSpecialAssessments',
        value: 'Yes'
      },
      groupKey: 'hoa'
    },
    {
      type: 'offset',
      cellSize: 12,
      style: { minHeight: 0 },
      groupKey: 'hoa'
    },
    {
      label: 'Capital Contribution',
      key: 'capitalContributionHasCapitalContribution',
      type: 'radio',
      options: YES_NO_OPTIONS,
      ...hoaColsDense,
      groupKey: 'hoa'
    },
    {
      type: 'offset',
      cellSize: isLayoutConstrained ? 4 : 6,
      style: { minHeight: '79px' },
      showWhenNot: {
        key: 'capitalContributionHasCapitalContribution',
        value: 'Yes'
      },
      groupKey: 'hoa'
    },
    {
      label: 'Capital Contribution Due Amount',
      key: 'capitalContributionDueAmount',
      type: 'currency-input',
      ...hoaColsDense,
      show: {
        key: 'capitalContributionHasCapitalContribution',
        value: 'Yes'
      },
      groupKey: 'hoa'
    },
    {
      type: 'offset',
      cellSize: 12,
      style: { minHeight: 0 },
      groupKey: 'hoa'
    },
    {
      label: 'Fees Due At Closing',
      key: 'hasFeesDueAtClosing',
      type: 'radio',
      options: YES_NO_OPTIONS,
      ...hoaColsDense,
      groupKey: 'hoa'
    },
    {
      type: 'offset',
      cellSize: 6,
      style: { minHeight: '79px' },
      showWhenNot: {
        key: 'hasFeesDueAtClosing',
        value: 'Yes'
      },
      groupKey: 'hoa'
    },
    {
      label: 'Fees Due At Closing Amount',
      key: 'feesDueAtClosingDueAmount',
      type: 'currency-input',
      ...hoaColsDense,
      show: {
        key: 'hasFeesDueAtClosing',
        value: 'Yes'
      },
      groupKey: 'hoa'
    },
    {
      label: 'Notes',
      key: 'notes',
      type: 'rich-text',
      cellSize: 12,
      height: '180px',
      enableLists: true,
      placeholder: 'Enter notes...',
      groupKey: 'notes'
    }
  ];

  const hoaSteps = [1, 2, 3].map(n => {
    const hoaName = hoaNameAt(n - 1);
    const tabName = formatHoaTabName(n, hoaName);
    return {
      key: `hoa${n}`,
      name: tabName,
      fields: [
        ...hoaFields,
        {
          label: formatHoaDocumentsLabel(n, hoaName),
          key: `hoa${n}Files`,
          type: 'file',
          cellSize: 12
        }
      ]
    };
  });

  if (hoaCount >= 1) {
    steps.push(hoaSteps[0]);
  }
  if (hoaCount >= 2) {
    steps.push(hoaSteps[1]);
  }
  if (hoaCount >= 3) {
    steps.push(hoaSteps[2]);
  }

  steps.push({
    key: 'invoices',
    name: 'Invoices',
    isComplete: false,
    cellSize: 12,
    fields: [
      {
        label: 'Fees',
        hideLabel: true,
        type: 'fixed-fields',
        cellSize: 12,
        groupKey: 'invoices',
        fields: [
          {
            label: 'Hard Cost',
            key: 'hardCost',
            type: 'currency-input',
            hideLabel: true,
            cellSize: 12,
            cellSizeMd: 2,
            groupKey: 'invoices'
          },
          {
            label: 'Processing Fee',
            key: 'estoppelProcessingFee',
            type: 'currency-input',
            hideLabel: true,
            cellSize: 12,
            cellSizeMd: 2,
            groupKey: 'invoices'
          },
          {
            label: 'Suggested Invoice Amount',
            key: 'suggestedInvoiceAmount',
            type: 'static',
            displayAs: 'currency',
            cellSize: 12,
            cellSizeMd: 2,
            note: values => {
              const min = Number(values?.minimumInvoicableAmount) || 0;
              const formatted = `$${min.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}`;
              return `Note: Minimum invoicable amount is ${formatted}.`;
            },
            groupKey: 'invoices',
            staticTone: values => (isInvoiceTotalBelowSuggested(values) ? 'error' : 'default'),
            staticTooltip: values => {
              if (!isInvoiceTotalBelowSuggested(values)) return null;
              const suggested = Number(values?.suggestedInvoiceAmount) || 0;
              const totalInvoiceAmount = (values?.invoices || []).reduce(
                (sum, row) => sum + (Number(row?.invoiceAmount) || 0),
                0
              );
              return `Suggested invoice amount ($${suggested}) is less than total invoice amount ($${totalInvoiceAmount}).`;
            },
            showStaticIcon: values => isInvoiceTotalBelowSuggested(values)
          }
        ]
      },
      {
        label: 'Invoices',
        key: 'invoices',
        hideLabel: true,
        type: 'multi-field',
        cellSize: 12,
        groupKey: 'invoices',
        className: 'px-4',
        fields: getInvoiceMultiFields({ isFilePopupOpen: isLayoutConstrained })
      },
      {
        label: 'Invoice Documents',
        key: 'invoicesFiles',
        type: 'file',
        cellSize: 12
      }
    ]
  });

  return steps;
};

const defaultHoaValues = {
  associationDuesDueAmount: '',
  associationDuesType: '',
  paidThroughChoice: PAID_THROUGH_DATE_OPTION,
  paidThrough: '',
  paidThroughText: '',
  specialAssessmentsHasSpecialAssessments: undefined,
  specialAssessmentsDueAmount: '',
  buyerApprovalRequired: undefined,
  collections: undefined,
  capitalContributionHasCapitalContribution: undefined,
  capitalContributionDueAmount: '',
  violations: undefined,
  openLitigation: undefined,
  hasFeesDueAtClosing: undefined,
  feesDueAtClosingDueAmount: '',
  notes: '',
  isComplete: false
};

const defaultValues = {
  invoices: {
    invoices: [{ ...EMPTY_INVOICE_ROW }],
    isComplete: false,
    hardCost: null,
    estoppelProcessingFee: null,
    minimumInvoicableAmount: null,
    suggestedInvoiceAmount: null
  },
  hoa1: { ...defaultHoaValues },
  hoa2: { ...defaultHoaValues },
  hoa3: { ...defaultHoaValues },
  documents: {
    isComplete: false,
    invoicesFiles: [],
    hoa1Files: [],
    hoa2Files: [],
    hoa3Files: []
  }
};

const hydrateEstoppelInvoiceFees = (invoices = {}, estoppelData) => {
  const root = estoppelData?.[0] || estoppelData || {};
  const estoppelOrder =
    (root?.orders || []).find(order => order?.orderType === ORDER_TYPES.ESTOPPEL || order?.orderType === 'Estoppel') ||
    {};
  const organization = root?.organization || {};

  const hardCost = estoppelOrder.hardCost ?? invoices.hardCost ?? 0;
  const estoppelProcessingFee =
    estoppelOrder.processingFee ?? organization.estoppelProcessingFee ?? invoices.estoppelProcessingFee ?? 0;
  const minimumInvoicableAmount = organization.estoppelMinimumInvoicableAmount ?? invoices.minimumInvoicableAmount ?? 0;

  return {
    ...invoices,
    hardCost,
    estoppelProcessingFee,
    minimumInvoicableAmount,
    suggestedInvoiceAmount: computeSuggestedInvoiceAmount(
      hardCost,
      estoppelProcessingFee,
      minimumInvoicableAmount
    )
  };
};

/** Maps API estoppel report document to sidebar form state (same pipeline as loading from estoppel.report). */
const buildFormStateFromEstoppelReport = (report, estoppelData = null) => {
  if (!report) return null;

  const mergedValues = { ...defaultValues };

  // Map invoices from backend
  const reportInvoices = report.invoices;
  if (reportInvoices && Array.isArray(reportInvoices.invoices) && reportInvoices.invoices.length) {
    mergedValues.invoices = {
      invoices: reportInvoices.invoices.map(inv => ({
        ...(inv._id != null && { _id: String(inv._id) }),
        ...(inv.serviceVersionId != null && {
          serviceVersionId: String(inv.serviceVersionId)
        }),
        invoiceNumber: inv.invoiceNumber ?? '',
        invoiceAmount: inv.invoiceAmount ?? '',
        paymentReceived: inv.paymentReceived ?? '',
        invoiceDueDate: inv.invoiceDueDate ?? '',
        status: inv.status || 'Unpaid'
      })),
      ...(reportInvoices.isComplete !== undefined && { isComplete: reportInvoices.isComplete }),
      ...(reportInvoices.hardCost !== undefined && { hardCost: reportInvoices.hardCost }),
      ...(reportInvoices.estoppelProcessingFee !== undefined && {
        estoppelProcessingFee: reportInvoices.estoppelProcessingFee
      }),
      ...(reportInvoices.minimumInvoicableAmount !== undefined && {
        minimumInvoicableAmount: reportInvoices.minimumInvoicableAmount
      }),
      ...(reportInvoices.suggestedInvoiceAmount !== undefined && {
        suggestedInvoiceAmount: reportInvoices.suggestedInvoiceAmount
      })
    };
  }
  mergedValues.invoices.isComplete = !!report.invoices.isComplete;

  // Map HOAs from backend to HOA 1/2/3 sections
  const hoas = report.hoas || [];

  ['hoa1', 'hoa2', 'hoa3'].forEach((sectionKey, index) => {
    const hoaData = hoas[index];
    if (!hoaData) return;

    mergedValues[sectionKey] = {
      ...defaultHoaValues,

      // association dues
      associationDuesDueAmount:
        hoaData.associationDues && typeof hoaData.associationDues.dueAmount === 'number'
          ? hoaData.associationDues.dueAmount
          : defaultHoaValues.associationDuesDueAmount,
      associationDuesType: hoaData.associationDues?.type || defaultHoaValues.associationDuesType,
      paidThroughChoice: (() => {
        const paidThroughText = String(hoaData.associationDues?.paidThroughText || '').trim();
        if (paidThroughText) return PAID_THROUGH_TEXT_OPTION;
        return PAID_THROUGH_DATE_OPTION;
      })(),
      paidThrough: hoaData.associationDues?.paidThrough || defaultHoaValues.paidThrough,
      paidThroughText: String(hoaData.associationDues?.paidThroughText || '').trim() || defaultHoaValues.paidThroughText,


      // special assessments
      specialAssessmentsHasSpecialAssessments: toYesNoValue(
        hoaData.specialAssessments?.hasSpecialAssessments
      ),
      specialAssessmentsDueAmount:
        hoaData.specialAssessments && typeof hoaData.specialAssessments.specialAssessmentsDueAmount === 'number'
          ? hoaData.specialAssessments.specialAssessmentsDueAmount
          : defaultHoaValues.specialAssessmentsDueAmount,

      // buyer approval (flat string first, legacy object as fallback)
      buyerApprovalRequired: toYesNoValue(
        hoaData.buyerApprovalRequired === 'Yes' || hoaData.buyerApprovalRequired === 'No'
          ? hoaData.buyerApprovalRequired
          : hoaData.buyerApprovalRequired?.hasBuyerApprovalRequired
      ),

      // collections (same idea)
      collections: toYesNoValue(
        hoaData.collections === 'Yes' || hoaData.collections === 'No'
          ? hoaData.collections
          : hoaData.collections?.hasCollections
      ),

      // capital contribution
      capitalContributionHasCapitalContribution: toYesNoValue(
        hoaData.capitalContribution?.hasCapitalContribution
      ),
      capitalContributionDueAmount:
        hoaData.capitalContribution && typeof hoaData.capitalContribution.capitalContributionDueAmount === 'number'
          ? hoaData.capitalContribution.capitalContributionDueAmount
          : defaultHoaValues.capitalContributionDueAmount,

      violations: toYesNoValue(hoaData.violations),
      openLitigation: toYesNoValue(hoaData.openLitigation),

      // fees due at closing
      hasFeesDueAtClosing: toYesNoValue(hoaData?.feesDueAtClosing?.hasFeesDueAtClosing),
      feesDueAtClosingDueAmount:
        hoaData?.feesDueAtClosing &&
        typeof hoaData?.feesDueAtClosing?.feesDueAtClosingDueAmount === 'number'
          ? hoaData?.feesDueAtClosing?.feesDueAtClosingDueAmount
          : defaultHoaValues?.feesDueAtClosingDueAmount,

      notes: normalizeRichTextValue(hoaData.notes || defaultHoaValues.notes),
      isComplete: !!hoaData.isComplete
    };

    // Map HOA files to documents tab
    mergedValues.documents[`hoa${index + 1}Files`] = Array.isArray(hoaData.files) ? hoaData.files : [];
  });

  // Map invoice documents if they exist in the report
  mergedValues.documents.invoicesFiles = report.invoices?.files || [];
  mergedValues.documents.isComplete = !!report.isDocumentSectionComplete;
  mergedValues.invoices = hydrateEstoppelInvoiceFees(mergedValues.invoices, estoppelData);

  const { propertyAddress, fileNo, folio, ownerName, buyerName } = report;
  return {
    mergedValues,
    fileInformation: { propertyAddress, fileNo, folio, ownerName, buyerName }
  };
};

/** Multipart save/generate when HOA edit modal has pending instruction PDFs (draft-only until report save). */
const buildEstoppelInstructionFileRequest = (payload, hoaSlots) => {
  if (!hasPendingHoaFiles(hoaSlots)) {
    return {
      requestBody: payload,
      contentType: 'application/json',
      stringify: true
    };
  }
  const formData = new FormData();
  appendHoaSlotFiles(formData, hoaSlots);
  formData.append('data', JSON.stringify(payload));
  return {
    requestBody: formData,
    contentType: 'multipart/form-data',
    stringify: false
  };
};

const AddEditEstoppelReportSidebar = ({
  show,
  onHide,
  estoppel,
  loading,
  service,
  onAfterSave,
  onOrderHoaUpdated,
  estoppelOrderId
}) => {
  const { openQa } = useQuery();
  const associationDuesTypes = useAppChoices('Association Dues Type');
  const associationDuesTypeOptions = useMemo(
    () => associationDuesTypes.map(value => ({ label: value, value })),
    [associationDuesTypes]
  );
  const role = useMemo(() => localStorage.getItem('user-role'), []);
  const canEditHoa = !isClient(role) && !loading;

  const [step, setStep] = useState(0);
  const [driveFiles, setDriveFiles] = useState(null);
  const [driveFilesLoading, setDriveFilesLoading] = useState(false);
  const [formValues, setFormValues] = useState(() => {
    const built = buildFormStateFromEstoppelReport(estoppel?.report, estoppel);
    return built?.mergedValues || JSON.parse(JSON.stringify(defaultValues));
  });
  const [fileInformation, setFileInformation] = useState({
    propertyAddress: '',
    fileNo: '',
    folio: '',
    ownerName: '',
    buyerName: '',
    closingDate: ''
  });
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [validationFieldErrors, setValidationFieldErrors] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const [fileShareMetadata, setFileShareMetadata] = useState(null);
  const [historyReport, setHistoryReport] = useState([]);
  const [confirmationModal, setConfirmationModal] = useState(null);
  const [qaAnalysisMeta, setQaAnalysisMeta] = useState({ show: false });
  const [qaFieldIssues, setQaFieldIssues] = useState({});
  const [qaSections, setQaSections] = useState(() => createEmptySections(ORDER_TYPES.ESTOPPEL));
  const [qaDocumentId, setQaDocumentId] = useState(null);
  const [qaAnalyzedAt, setQaAnalyzedAt] = useState(null);
  const [activeQaIssueId, setActiveQaIssueId] = useState(null);
  const [pendingQaScrollField, setPendingQaScrollField] = useState(null);
  const [hoaSlotCount, setHoaSlotCount] = useState(() => deriveHoaSlotCountFromEstoppel(estoppel));
  const [hoaSlotReportRefs, setHoaSlotReportRefs] = useState(() =>
    buildHoaSlotRefsFromReport(estoppel?.report, deriveHoaSlotCountFromEstoppel(estoppel))
  );

  // Track unsaved changes
  const [showUnsavedChangesAlert, setShowUnsavedChangesAlert] = useState(false);
  const [initialFormState, setInitialFormState] = useState(null);
  /** Draft HOA slots (Edit modal) — persisted to association only on report save/preview. */
  const [orderHoaDisplayOverride, setOrderHoaDisplayOverride] = useState(null);
  /** Draft explanations per HOA index (synced to order on report save). */
  const [localNoEstoppelExplanations, setLocalNoEstoppelExplanations] = useState(() =>
    buildExplanationsFromSlots(
      resolveOrderHoaDisplaySlots(estoppel, service, estoppelOrderId, null),
      deriveHoaSlotCountFromEstoppel(estoppel)
    )
  );
  const [showUpdateHoaModal, setShowUpdateHoaModal] = useState(false);
  const [updateHoaEditIndex, setUpdateHoaEditIndex] = useState(null);

  const orderHoaDisplay = useMemo(() => {
    const source = resolveOrderHoaDisplaySlots(
      estoppel,
      service,
      estoppelOrderId,
      orderHoaDisplayOverride
    );
    return padHoaDisplaySlots(source, hoaSlotCount);
  }, [
    estoppel,
    service,
    estoppelOrderId,
    orderHoaDisplayOverride,
    hoaSlotCount,
    estoppel?.report?.orderId?.homeOwnerInformation,
    service?.orders
  ]);

  const editingEstoppelOrder = useMemo(() => {
    const fromService = service?.orders?.find(
      o => o?._id && String(o._id) === String(estoppelOrderId)
    );
    if (fromService) {
      return {
        ...fromService,
        homeOwnerInformation: orderHoaDisplay
      };
    }
    return {
      _id: estoppelOrderId,
      orderType: 'Estoppel',
      homeOwnerInformation: orderHoaDisplay
    };
  }, [service?.orders, estoppelOrderId, orderHoaDisplay]);

  const applyEnrichedHoaSlots = (slots, slotCount = hoaSlotCount) => {
    if (!Array.isArray(slots)) return;
    const count = Math.min(3, Math.max(1, slots.length || slotCount || 1));
    setOrderHoaDisplayOverride(slots);
    setLocalNoEstoppelExplanations(buildExplanationsFromSlots(slots, count));
  };

  /** Pull latest association/company fields onto the report HOA display. */
  const refreshHoaDisplayFromAssociation = async () => {
    if (!estoppelOrderId) return;

    if (service?._id) {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.FETCH_SERVICES,
        requestBody: {
          filter: { serviceFilters: { _id: service._id } }
        }
      });
      if (!error && Array.isArray(response) && response[0]?.orders) {
        const order = response[0].orders.find(
          o => o?._id && String(o._id) === String(estoppelOrderId)
        );
        if (Array.isArray(order?.homeOwnerInformation)) {
          applyEnrichedHoaSlots(order.homeOwnerInformation);
          return;
        }
      }
    }

    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.ESTOPPEL_REPORT_BASE,
      method: 'POST',
      requestBody: { orderId: estoppelOrderId }
    });
    if (error) return;
    const slots = response?.orderId?.homeOwnerInformation;
    if (Array.isArray(slots)) applyEnrichedHoaSlots(slots);
  };

  const onPreviewHistory = () => {
    setShowHistory(true);
  };

  const onCloseHistory = () => {
    setShowHistory(false);
  };

  const onFileShareClick = () => {
    setFileShareMetadata({});
  };

  // Helper function to remove backend-specific fields
  const cleanFormStateForComparison = state => {
    const cleaned = JSON.parse(JSON.stringify(state));

    BACKEND_FIELDS_TO_EXCLUDE.forEach(field => {
      delete cleaned[field];
    });

    return cleaned;
  };

  // Helper function to normalize fileInformation for comparison
  const normalizeFileInformation = fileInfo => {
    const { propertyAddress, fileNo, folio, ownerName, buyerName, closingDate } = fileInfo;
    return {
      propertyAddress,
      fileNo,
      folio,
      ownerName,
      buyerName,
      closingDate: toClosingDateInputValue(closingDate)
    };
  };

  const withServiceClosingDate = (fileInfo, closingDateOverride) => ({
    ...fileInfo,
    closingDate:
      toClosingDateInputValue(closingDateOverride) ||
      toClosingDateInputValue(fileInfo?.closingDate) ||
      toClosingDateInputValue(service?.closingDate)
  });

  // Helper function to update initial state after save/preview
  const updateInitialFormState = () => {
    const cleanedFormValues = cleanFormStateForComparison(formValues);
    const normalizedFileInfo = normalizeFileInformation(fileInformation);
    setInitialFormState({
      formValues: cleanedFormValues,
      fileInformation: normalizedFileInfo,
      hoaExplanations: localNoEstoppelExplanations.map(e => String(e ?? '').trim()),
      hoaDirectory: normalizeHoaDirectorySnapshot(orderHoaDisplay, hoaSlotCount)
    });
  };

  const onReportGenerationSuccess = async savedReport => {
    if (savedReport) {
      const built = buildFormStateFromEstoppelReport(savedReport, null);
      if (built) {
        setFormValues(built.mergedValues);
        setFileInformation(withServiceClosingDate(built.fileInformation, fileInformation.closingDate));

        const cleanedFormValues = cleanFormStateForComparison(built.mergedValues);
        const enrichedFromReport = savedReport?.orderId?.homeOwnerInformation;
        let syncedSlots = null;
        if (Array.isArray(enrichedFromReport) && enrichedFromReport.length) {
          applyEnrichedHoaSlots(enrichedFromReport);
          syncedSlots = enrichedFromReport;
        } else {
          await refreshHoaDisplayFromAssociation();
          syncedSlots = null;
        }
        const { slots, rows, hoaExplanations, hoaDirectory } = buildHoaUiState(
          estoppel,
          service,
          estoppelOrderId,
          { savedReport, override: syncedSlots }
        );
        setLocalNoEstoppelExplanations(buildExplanationsFromSlots(rows, slots));
        const nextFileInfo = withServiceClosingDate(built.fileInformation, fileInformation.closingDate);
        setInitialFormState({
          formValues: cleanedFormValues,
          fileInformation: normalizeFileInformation(nextFileInfo),
          hoaExplanations,
          hoaDirectory
        });
        setHoaSlotCount(slots);
        setHoaSlotReportRefs(buildHoaSlotRefsFromReport(savedReport, slots));
      }
    } else {
      // Preview / quick preview — re-sync HOA display from association directory
      await refreshHoaDisplayFromAssociation();
      updateInitialFormState();
    }
    onAfterSave?.();
  };

  // check if there are unsaved changes
  // Order HOA directory fields persist immediately via Update HOA — do not treat them as report dirty.
  const checkForUnsavedChanges = () => {
    if (!initialFormState) return false;

    const cleanedCurrentFormValues = cleanFormStateForComparison(formValues);
    const normalizedFileInfo = normalizeFileInformation(fileInformation);
    const formDirty = !isEqual(
      { formValues: cleanedCurrentFormValues, fileInformation: normalizedFileInfo },
      { formValues: initialFormState.formValues, fileInformation: initialFormState.fileInformation }
    );
    const hoaDirty = !isEqual(
      localNoEstoppelExplanations.map(e => String(e ?? '').trim()),
      initialFormState.hoaExplanations || []
    );
    return formDirty || hoaDirty;
  };

  const hasUnsavedChanges = useMemo(
    () => checkForUnsavedChanges(),
    [formValues, fileInformation, initialFormState, localNoEstoppelExplanations]
  );

  // close with unsaved changes check
  const handleClose = () => {
    if (checkForUnsavedChanges()) {
      setShowUnsavedChangesAlert(true);
    } else {
      onHide();
    }
  };

  // save unsaved changes and continue
  const handleSaveAndContinue = async () => {
    const saveResult = await onSaveReport();

    // save successful
    if (saveResult === true) {
      setShowUnsavedChangesAlert(false);
      onHide();
    } else {
      // save failed
      setShowUnsavedChangesAlert(false);
    }
  };

  // discard unsaved changes and continue
  const handleDiscardChanges = () => {
    setShowUnsavedChangesAlert(false);
    onHide();
  };

  // cancel unsaved changes and continue
  const handleCancelAction = () => {
    setShowUnsavedChangesAlert(false);
  };

  // toggle step completion
  const handleToggleStepCompletion = (sectionKey, isComplete) => {
    setFormValues(prev => ({
      ...prev,
      [sectionKey]: {
        ...(prev[sectionKey] || {}),
        isComplete: isComplete
      }
    }));
  };

  const loadDriveFiles = async folderId => {
    setDriveFilesLoading(true);
    const { response, error } = await makeRESTApiRequest({
      method: 'GET',
      endpoint: `${ENDPOINTS.GOOGLE_DRIVE_READ_FOLDER}?folderId=${folderId}`
    });

    setDriveFilesLoading(false);

    if (error) {
      toast.error('Failed to load drive files');
      return;
    }

    setDriveFiles(response);
  };

  useEffect(() => {
    if (estoppel?.report) {
      const built = buildFormStateFromEstoppelReport(estoppel.report, estoppel);
      if (!built) return;

      setFormValues(built.mergedValues);
      const nextFileInfo = withServiceClosingDate(built.fileInformation);
      setFileInformation(nextFileInfo);

      const cleanedFormValues = cleanFormStateForComparison(built.mergedValues);
      const { slots, rows, hoaExplanations, hoaDirectory, reportForRefs } = buildHoaUiState(
        estoppel,
        service,
        estoppelOrderId,
        { override: orderHoaDisplayOverride }
      );
      setLocalNoEstoppelExplanations(buildExplanationsFromSlots(rows, slots));
      setInitialFormState({
        formValues: cleanedFormValues,
        fileInformation: normalizeFileInformation(nextFileInfo),
        hoaExplanations,
        hoaDirectory
      });
      setHoaSlotCount(slots);
      setHoaSlotReportRefs(buildHoaSlotRefsFromReport(reportForRefs, slots));
    }
  }, [estoppel]);

  // browser refresh/close unsaved changes alert
  useEffect(() => {
    const handleBeforeUnload = e => {
      if (show && checkForUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = ''; // Required for Chrome
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [show, formValues, fileInformation, initialFormState, localNoEstoppelExplanations, orderHoaDisplay, hoaSlotCount]);

  const onTabSelect = key => {
    const index = formSteps.findIndex(s => s.key === key);
    if (index !== -1) setStep(index);
  };

  const addHoaTab = () => {
    setHoaSlotCount(c => {
      if (c >= 3) return c;
      setHoaSlotReportRefs(prevRefs => [...prevRefs, {}]);
      setOrderHoaDisplayOverride(prev => {
        const source = Array.isArray(prev) && prev.length ? prev : orderHoaDisplay;
        return [...padHoaDisplaySlots(source, c), {}];
      });
      setFormValues(prevFv => {
        if (c === 1) {
          return {
            ...prevFv,
            hoa2: { ...defaultHoaValues },
            documents: { ...prevFv.documents, hoa2Files: [] }
          };
        }
        return {
          ...prevFv,
          hoa3: { ...defaultHoaValues },
          documents: { ...prevFv.documents, hoa3Files: [] }
        };
      });
      setLocalNoEstoppelExplanations(prev => [...prev, '']);
      return c + 1;
    });
  };

  /** Removes highest hoa tab index */
  const applyRemoveHoaTab = (stepKey, slotCountAtOpen) => {
    if (slotCountAtOpen <= 1) return;
    const isRemovable =
      (slotCountAtOpen === 3 && stepKey === 'hoa3') ||
      (slotCountAtOpen === 2 && stepKey === 'hoa2');
    if (!isRemovable) return;

    const nextCount = Math.max(1, slotCountAtOpen - 1);
    setHoaSlotCount(nextCount);
    setHoaSlotReportRefs(prev => prev.slice(0, -1));
    setLocalNoEstoppelExplanations(prev => prev.slice(0, -1));
    setOrderHoaDisplayOverride(prev => {
      const source = Array.isArray(prev) && prev.length ? prev : orderHoaDisplay;
      return padHoaDisplaySlots(source, slotCountAtOpen).slice(0, nextCount);
    });

    if (slotCountAtOpen === 3) {
      setFormValues(prev => ({
        ...prev,
        hoa3: { ...defaultHoaValues },
        documents: { ...prev.documents, hoa3Files: [] }
      }));
    } else {
      setFormValues(prev => ({
        ...prev,
        hoa2: { ...defaultHoaValues },
        documents: { ...prev.documents, hoa2Files: [] }
      }));
    }
  };

  const confirmRemoveHoaTab = stepKey => {
    const slotCountAtOpen = hoaSlotCount;
    if (slotCountAtOpen <= 1) return;
    const isRemovable =
      (slotCountAtOpen === 3 && stepKey === 'hoa3') ||
      (slotCountAtOpen === 2 && stepKey === 'hoa2');
    if (!isRemovable) return;

    setConfirmationModal({
      title: 'Remove HOA tab?',
      alertText: 'Are you sure you want to remove this HOA?',
      continueButtonText: 'Remove',
      dismissButtonText: 'Cancel',
      continueButtonVariant: 'danger',
      onContinue: () => {
        applyRemoveHoaTab(stepKey, slotCountAtOpen);
        setConfirmationModal(null);
      }
    });
  };

  const reorderHoaSlots = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const slotCount = hoaSlotCount;
    if (fromIndex < 0 || toIndex < 0 || fromIndex >= slotCount || toIndex >= slotCount) return;

    setHoaSlotReportRefs(prev =>
      reorderListByIndex(prev.slice(0, slotCount), fromIndex, toIndex)
    );
    setLocalNoEstoppelExplanations(prev =>
      reorderListByIndex(prev.slice(0, slotCount), fromIndex, toIndex)
    );
    setOrderHoaDisplayOverride(prev => {
      const source = Array.isArray(prev) ? prev : orderHoaDisplay;
      return reorderListByIndex(source.slice(0, slotCount), fromIndex, toIndex);
    });

    setFormValues(prev => {
      const hoaData = HOA_SECTION_KEYS.slice(0, slotCount).map(
        key => prev[key] || { ...defaultHoaValues }
      );
      const fileData = HOA_FILE_KEYS.slice(0, slotCount).map(
        key => prev.documents?.[key] || []
      );
      const reorderedHoa = reorderListByIndex(hoaData, fromIndex, toIndex);
      const reorderedFiles = reorderListByIndex(fileData, fromIndex, toIndex);
      const next = { ...prev, documents: { ...prev.documents } };
      for (let i = 0; i < slotCount; i++) {
        next[HOA_SECTION_KEYS[i]] = reorderedHoa[i];
        next.documents[HOA_FILE_KEYS[i]] = reorderedFiles[i];
      }
      return next;
    });

    const currentStepKey = formSteps[step]?.key || '';
    if (/^hoa[1-3]$/.test(currentStepKey)) {
      const currentSlotIndex = Number(currentStepKey.replace('hoa', '')) - 1;
      const nextSlotIndex = getHoaSlotIndexAfterReorder(
        slotCount,
        fromIndex,
        toIndex,
        currentSlotIndex
      );
      if (nextSlotIndex !== -1 && nextSlotIndex !== currentSlotIndex) {
        const nextStepKey = `hoa${nextSlotIndex + 1}`;
        const nextStepIndex = formSteps.findIndex(s => s.key === nextStepKey);
        if (nextStepIndex !== -1) setStep(nextStepIndex);
      }
    }
  };

  const prepareReportData = () => {
    const reportData = {};

    // Invoices
    if (formValues.invoices) {
      const rawInvoices = formValues.invoices.invoices || [];
      reportData.invoices = {
        invoices: rawInvoices.map(inv => ({
          ...(inv._id != null && { _id: inv._id }),
          ...(inv.serviceVersionId != null && { serviceVersionId: inv.serviceVersionId }),
          invoiceNumber: inv.invoiceNumber || '',
          invoiceAmount:
            inv.invoiceAmount !== undefined && inv.invoiceAmount !== null && inv.invoiceAmount !== ''
              ? Number(inv.invoiceAmount)
              : undefined,
          paymentReceived:
            inv.paymentReceived !== undefined && inv.paymentReceived !== null && inv.paymentReceived !== ''
              ? Number(inv.paymentReceived)
              : undefined,
          invoiceDueDate: inv.invoiceDueDate || undefined,
          status: inv.status || 'Unpaid'
        })),
        files: formValues.documents?.invoicesFiles || [],
        hardCost:
          formValues.invoices.hardCost !== undefined &&
          formValues.invoices.hardCost !== null &&
          formValues.invoices.hardCost !== ''
            ? Number(formValues.invoices.hardCost)
            : undefined,
        estoppelProcessingFee:
          formValues.invoices.estoppelProcessingFee !== undefined &&
          formValues.invoices.estoppelProcessingFee !== null &&
          formValues.invoices.estoppelProcessingFee !== ''
            ? Number(formValues.invoices.estoppelProcessingFee)
            : undefined,
        minimumInvoicableAmount:
          formValues.invoices.minimumInvoicableAmount !== undefined &&
          formValues.invoices.minimumInvoicableAmount !== null &&
          formValues.invoices.minimumInvoicableAmount !== ''
            ? Number(formValues.invoices.minimumInvoicableAmount)
            : undefined,
        suggestedInvoiceAmount: computeSuggestedInvoiceAmount(
          formValues.invoices.hardCost,
          formValues.invoices.estoppelProcessingFee,
          formValues.invoices.minimumInvoicableAmount
        ),
        isComplete: !!formValues.invoices.isComplete
      };
    }

    // HOAs
    const hoas = [];
    const hoaSections = ['hoa1', 'hoa2', 'hoa3'];

    hoaSections.forEach((sectionKey, index) => {
      if (index >= hoaSlotCount) return;
      const section = formValues[sectionKey] || {};

      const slotRef = hoaSlotReportRefs[index] || {};
      const reportHoaId = slotRef._id;
      const orderHoaId = slotRef.orderHoaId;
      const displaySlot = {
        ...(orderHoaDisplay[index] || {}),
        noEstoppelNeededExplanation:
          localNoEstoppelExplanations[index] != null
            ? String(localNoEstoppelExplanations[index])
            : orderHoaDisplay[index]?.noEstoppelNeededExplanation
      };
      const noEstoppelNeeded = !!displaySlot.noEstoppelNeeded;

      if (noEstoppelNeeded) {
        const syncFields = hoaDirectoryFieldsForOrderSyncPayload(displaySlot);
        const tabSelected =
          displaySlot.managementType === 'self' ||
          displaySlot.managementType === 'managed';
        if (tabSelected) {
          syncFields.managementType = displaySlot.managementType;
        } else {
          syncFields.managementType = null;
        }
        hoas.push({
          ...(reportHoaId && { _id: reportHoaId }),
          ...(orderHoaId && { orderHoaId }),
          ...syncFields,
          noEstoppelNeeded: true,
          files: formValues.documents?.[`hoa${index + 1}Files`] || [],
          notes: '',
          isComplete: !!section.isComplete
        });
        return;
      }

      hoas.push({
        ...(reportHoaId && { _id: reportHoaId }),
        ...(orderHoaId && { orderHoaId }),
        ...hoaDirectoryFieldsForOrderSyncPayload(displaySlot),
        associationDues: {
          dueAmount:
            section.associationDuesDueAmount !== undefined &&
            section.associationDuesDueAmount !== null &&
            section.associationDuesDueAmount !== ''
              ? Number(section.associationDuesDueAmount)
              : undefined,
          type: section.associationDuesType || undefined,
          ...(section.paidThroughChoice === PAID_THROUGH_TEXT_OPTION
            ? {
                paidThrough: null,
                paidThroughText: String(section.paidThroughText || '').trim() || null
              }
            : {
                paidThrough: section.paidThrough || null,
                paidThroughText: null
              })
        },
        specialAssessments: {
          hasSpecialAssessments: toYesNoValue(section.specialAssessmentsHasSpecialAssessments),
          specialAssessmentsDueAmount:
            section.specialAssessmentsHasSpecialAssessments === 'Yes' &&
            section.specialAssessmentsDueAmount !== undefined &&
            section.specialAssessmentsDueAmount !== null &&
            section.specialAssessmentsDueAmount !== ''
              ? Number(section.specialAssessmentsDueAmount)
              : undefined
        },
        buyerApprovalRequired: toYesNoValue(section.buyerApprovalRequired),
        collections: toYesNoValue(section.collections),
        capitalContribution: {
          hasCapitalContribution: toYesNoValue(section.capitalContributionHasCapitalContribution),
          capitalContributionDueAmount:
            section.capitalContributionHasCapitalContribution === 'Yes' &&
            section.capitalContributionDueAmount !== undefined &&
            section.capitalContributionDueAmount !== null &&
            section.capitalContributionDueAmount !== ''
              ? Number(section.capitalContributionDueAmount)
              : undefined
        },
        violations: toYesNoValue(section.violations),
        openLitigation: toYesNoValue(section.openLitigation),
        feesDueAtClosing: {
          hasFeesDueAtClosing: toYesNoValue(section.hasFeesDueAtClosing),
          feesDueAtClosingDueAmount:
            section.hasFeesDueAtClosing === 'Yes' &&
            section.feesDueAtClosingDueAmount !== undefined &&
            section.feesDueAtClosingDueAmount !== null &&
            section.feesDueAtClosingDueAmount !== ''
              ? Number(section.feesDueAtClosingDueAmount)
              : undefined
        },
        files: formValues.documents?.[`hoa${index + 1}Files`] || [],
        notes: normalizeRichTextValue(section.notes || ''),
        isComplete: !!section.isComplete
      });
    });

    if (hoas.length) {
      reportData.hoas = hoas;
    }

    reportData.isDocumentSectionComplete = !!formValues.documents?.isComplete;

    return reportData;
  };

  const hoaSlotsForInstructionUpload = useMemo(
    () => orderHoaDisplay.slice(0, hoaSlotCount),
    [orderHoaDisplay, hoaSlotCount]
  );

  // Use report generation hook
  const {
    isSaving,
    isGeneratingReport,
    reportGenerationMode,
    generatedFile,
    onSaveReport,
    onGenerateReport,
    onQuickPreview,
    onRefreshFile,
    handleHideGeneratedFile
  } = useReportGeneration({
    endpoints: {
      save: ENDPOINTS.ESTOPPEL_REPORT_WITH_ID,
      generate: ENDPOINTS.GENERATE_ESTOPPEL_REPORT
    },
    getEntityId: () => estoppelOrderId,
    getReportId: () => estoppel?.report?._id || estoppel?.reportId,
    prepareReportData,
    fileInformation,
    onSuccess: onReportGenerationSuccess,
    onValidationError: ({ fieldErrors = {} } = {}) => setValidationFieldErrors(fieldErrors),
    downloadFilePrefix: 'Estoppel_Report',
    buildSaveRequest: ({ saveData, entityId, fileInformation: fileInfo }) =>
      buildEstoppelInstructionFileRequest(
        {
          orderId: entityId,
          ...saveData,
          ...fileInfo,
          skipValidation: true
        },
        hoaSlotsForInstructionUpload
      ),
    buildGenerateRequest: ({
      reportData,
      entityId,
      fileInformation: fileInfo,
      reportId,
      appendFiles,
      skipValidation
    }) =>
      buildEstoppelInstructionFileRequest(
        {
          _id: reportId,
          orderId: entityId,
          ...fileInfo,
          ...reportData,
          appendFiles,
          skipValidation
        },
        hoaSlotsForInstructionUpload
      )
  });

  const isFilePopupOpen = generatedFile ? true : false;
  const isQaSidebarOpen = qaAnalysisMeta?.show;
  const isLayoutConstrained = isFilePopupOpen || isQaSidebarOpen;
  const formSteps = useMemo(
    () => getFormSteps(isLayoutConstrained, hoaSlotCount, associationDuesTypeOptions, orderHoaDisplay),
    [isLayoutConstrained, hoaSlotCount, associationDuesTypeOptions, orderHoaDisplay]
  );

  const reportOrderId = useMemo(
    () => resolveReportOrderId(estoppel?.report?.orderId) || estoppelOrderId,
    [estoppel?.report?.orderId, estoppelOrderId]
  );

  const closeQaPanel = useCallback(() => {
    setQaAnalysisMeta(prev => ({ ...prev, show: false }));
  }, []);

  const openQaPanel = useCallback(() => {
    setQaAnalysisMeta(prev => ({ ...prev, show: true }));
  }, []);

  useEffect(() => {
    if (!show || !reportOrderId) {
      setQaSections(createEmptySections(ORDER_TYPES.ESTOPPEL));
      setQaFieldIssues({});
      setQaDocumentId(null);
      setQaAnalyzedAt(null);
      return;
    }

    let cancelled = false;

    const loadQaIssues = async () => {
      const { sections, qaDocumentId: docId, qaDocument, error } = await fetchReportQaSectionsByOrder(
        reportOrderId,
        ORDER_TYPES.ESTOPPEL
      );
      if (cancelled) return;

      if (error) {
        toast.error(error);
        return;
      }

      setQaSections(sections);
      setQaDocumentId(docId);
      setQaAnalyzedAt(qaDocument?.lastAnalyzedAt || qaDocument?.updatedAt || null);
      setQaFieldIssues(buildQaFieldIssueMap(sections));
    };

    loadQaIssues();

    return () => {
      cancelled = true;
    };
  }, [show, reportOrderId]);

  const handleQaIssuesChange = useCallback(sections => {
    setQaSections(sections);
    setQaFieldIssues(buildQaFieldIssueMap(sections));
  }, []);

  const qaFieldStatuses = useMemo(() => buildFieldStatusMapFromQaIssues(qaFieldIssues), [qaFieldIssues]);
  const hasQaFieldStatuses = Object.keys(qaFieldStatuses).length > 0;
  const qaUnresolvedStepKeys = useMemo(
    () => buildUnresolvedQaIssueStepKeys(qaSections, ORDER_TYPES.ESTOPPEL),
    [qaSections]
  );

  const handleQaIssueClick = useCallback(
    (issue, sectionKey) => {
      setActiveQaIssueId(issue.id);

      const navigation = resolveQaIssueNavigation({
        sectionKey,
        fieldNames: issue.fieldNames || [],
        orderType: ORDER_TYPES.ESTOPPEL
      });
      if (!navigation) return;

      const { stepKey, fieldKey } = navigation;
      if (!stepKey) {
        scrollToFieldKey(fieldKey);
        return;
      }

      const index = formSteps.findIndex(s => s.key === stepKey);
      if (index !== -1) {
        setStep(index);
        setPendingQaScrollField(fieldKey);
      }
    },
    [formSteps]
  );

  useEffect(() => {
    if (!pendingQaScrollField) return;
    scrollToFieldKey(pendingQaScrollField);
    setPendingQaScrollField(null);
  }, [step, pendingQaScrollField]);

  const validationFieldStatuses = useMemo(() => {
    const keys = validationFieldErrors[formSteps[step]?.key] || [];
    return Object.fromEntries(keys.map(key => [key, 'error']));
  }, [validationFieldErrors, formSteps, step]);

  const mergedFieldStatuses = useMemo(
    () => ({ ...qaFieldStatuses, ...validationFieldStatuses }),
    [qaFieldStatuses, validationFieldStatuses]
  );

  // No-estoppel HOA tabs: only the document file picker (skip financial fields).
  const activeStepFields = useMemo(() => {
    const fields = formSteps[step]?.fields || [];
    const hoaIndex = HOA_SECTION_KEYS.indexOf(formSteps[step]?.key);
    if (hoaIndex >= 0 && orderHoaDisplay[hoaIndex]?.noEstoppelNeeded) {
      return fields.filter(f => f.type === 'file');
    }
    return fields;
  }, [formSteps, step, orderHoaDisplay]);

  // Clamp step index when formSteps shrinks (e.g. hoaCount decreases)
  useEffect(() => {
    if (step >= formSteps.length) {
      setStep(formSteps.length - 1);
    }
  }, [formSteps.length]);

  useEffect(() => {
    if (!show) {
      handleHideGeneratedFile();
      setDriveFiles(null); // Clear cached files when sidebar closes
      setInitialFormState(null);
      setValidationFieldErrors({});
      closeQaPanel();
      setOrderHoaDisplayOverride(null);
      setShowUpdateHoaModal(false);
      setUpdateHoaEditIndex(null);
      return;
    }

    if (openQa === 'true') {
      openQaPanel();
    }

    // Load drive files when sidebar opens
    if (show && service?.driveFolder && !driveFiles && !driveFilesLoading) {
      loadDriveFiles(service.driveFolder);
    }
  }, [show, openQa, service?.driveFolder, handleHideGeneratedFile, closeQaPanel, openQaPanel]);

  const onHistoryReportChange = newReports => {
    setHistoryReport([...newReports]);
  };

  useEffect(() => {
    setHistoryReport(estoppel?.report?.generatedFiles || []);
  }, [estoppel?.report?.generatedFiles]);

  return (
    <>
      <SlidingSideBar
        fullScreen
        visible={show}
        onClose={handleClose}
        title={`${estoppel?.reportId ? 'Update' : 'Start'} Report`}
        contentStyle={{ position: 'relative' }}
        width={generatedFile ? '50%' : '100%'}
        style={generatedFile ? { background: 'transparent' } : undefined}
        dock="left"
      >
        {loading ? (
          <div className="d-flex justify-content-start align-items-center">
            <Loader />
          </div>
        ) : (
          <>
            <Card className="p-3 my-1 w-100">
              <div className="px-3">
                <Row className="p-0">
                  <Col className="p-0 pr-2" xs={isLayoutConstrained ? 6 : true}>
                    <FieldStatusShell fieldKey="propertyAddress" statuses={qaFieldStatuses} label="Property Address">
                      {styles => (
                        <FormControl
                          size="lg"
                          className="midFont"
                          type="text"
                          value={fileInformation.propertyAddress}
                          onChange={e => setFileInformation({ ...fileInformation, propertyAddress: e.target.value })}
                          style={styles?.input}
                        />
                      )}
                    </FieldStatusShell>
                  </Col>
                  <Col className={`p-0 ${isLayoutConstrained ? '' : 'pr-2'}`} xs={isLayoutConstrained ? 6 : true}>
                    <FieldStatusShell fieldKey="folio" statuses={qaFieldStatuses} label="Folio No">
                      {styles => (
                        <FolioInputWithCopy
                          value={fileInformation.folio}
                          onChange={e => setFileInformation({ ...fileInformation, folio: e.target.value })}
                          style={styles?.input}
                        />
                      )}
                    </FieldStatusShell>
                  </Col>
                  <Col className="p-0 pr-2" xs={isLayoutConstrained ? 6 : true}>
                    <FieldStatusShell fieldKey="fileNo" statuses={qaFieldStatuses} label="Customer File">
                      {styles => (
                        <FormControl
                          size="lg"
                          className="midFont"
                          type="text"
                          value={fileInformation.fileNo}
                          onChange={e => setFileInformation({ ...fileInformation, fileNo: e.target.value })}
                          style={styles?.input}
                        />
                      )}
                    </FieldStatusShell>
                  </Col>
                  <Col className={`p-0 ${isLayoutConstrained ? '' : 'pr-2'}`} xs={isLayoutConstrained ? 6 : true}>
                    <FieldStatusShell fieldKey="ownerName" statuses={qaFieldStatuses} label="Owner Name">
                      {styles => (
                        <FormControl
                          size="lg"
                          className="midFont"
                          type="text"
                          value={fileInformation.ownerName}
                          onChange={e => setFileInformation({ ...fileInformation, ownerName: e.target.value })}
                          style={styles?.input}
                        />
                      )}
                    </FieldStatusShell>
                  </Col>
                  <Col className="p-0 pr-2" xs={isLayoutConstrained ? 6 : true}>
                    <FieldStatusShell fieldKey="buyerName" statuses={qaFieldStatuses} label="Buyer Name">
                      {styles => (
                        <FormControl
                          size="lg"
                          className="midFont"
                          type="text"
                          value={fileInformation.buyerName}
                          onChange={e => setFileInformation({ ...fileInformation, buyerName: e.target.value })}
                          style={styles?.input}
                        />
                      )}
                    </FieldStatusShell>
                  </Col>
                  <Col className="p-0" xs={isLayoutConstrained ? 6 : true}>
                    <FieldStatusShell fieldKey="closingDate" statuses={qaFieldStatuses} label="Closing Date">
                      {styles => (
                        <FormControl
                          size="lg"
                          className="midFont"
                          type="date"
                          max="9999-12-31"
                          value={toClosingDateInputValue(fileInformation.closingDate)}
                          onChange={e => {
                            const val = e.target.value;
                            if (!val) {
                              setFileInformation({ ...fileInformation, closingDate: '' });
                              return;
                            }
                            const year = val.split('-')[0];
                            if (year?.length > 4) return;
                            setFileInformation({ ...fileInformation, closingDate: val });
                          }}
                          style={styles?.input}
                        />
                      )}
                    </FieldStatusShell>
                  </Col>
                </Row>
              </div>
            </Card>
            <div className="p-0 mt-4 mb-5 d-flex flex-row gap-2 align-items-stretch w-100">
              <div
                className="flex-grow-1"
                style={{
                  minWidth: 0,
                  flex: isQaSidebarOpen ? '1 1 70%' : '1 1 auto',
                  maxWidth: isQaSidebarOpen ? '70%' : '100%'
                }}
              >
              <StepTabNavigation
                formSteps={formSteps}
                currentStep={step}
                formValues={formValues}
                onTabSelect={onTabSelect}
                onToggleStepCompletion={handleToggleStepCompletion}
                onHoaTabReorder={hoaSlotCount > 1 ? reorderHoaSlots : null}
                onQaAnalysisClick={openQaPanel}
                qaAnalysisMeta={qaAnalysisMeta}
                qaUnresolvedStepKeys={qaUnresolvedStepKeys}
                tabSuffix={(s, stepIsComplete) => {
                  const showRemoveHoa3 = s.key === 'hoa3' && hoaSlotCount === 3;
                  const showRemoveHoa2 = s.key === 'hoa2' && hoaSlotCount === 2;
                  if (!showRemoveHoa3 && !showRemoveHoa2) return null;
                  const hoaNum = showRemoveHoa3 ? '3' : '2';
                  return (
                    <button
                      type="button"
                      className="p-0 ml-1 border-0 bg-transparent d-inline-flex align-items-center justify-content-center"
                      aria-label={`Remove HOA ${hoaNum}`}
                      onClick={e => {
                        e.stopPropagation();
                        confirmRemoveHoaTab(s.key);
                      }}
                    >
                      <X size={16} className={stepIsComplete ? 'text-white' : 'text-muted'} />
                    </button>
                  );
                }}
                trailingAfterStepKey={hoaSlotCount < 3 ? `hoa${hoaSlotCount}` : null}
                trailingInlineContent={
                  hoaSlotCount < 3 ? (
                    <div
                      className="px-3 py-2 hover-light cursor-pointer flex-shrink-0"
                      style={{ boxSizing: 'border-box', borderBottom: 'none' }}
                      onClick={addHoaTab}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          addHoaTab();
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label="Add HOA tab"
                      title="Add HOA (up to 3 total)"
                    >
                      <div
                        className="d-flex align-items-center justify-content-center gap-2 px-2 "
                        style={{ borderRadius: '8px', border: '1px dashed #adb5bd' }}
                      >
                        <span className="d-inline-flex align-items-center justify-content-center p-0" aria-hidden>
                          <Plus size={16} className="text-muted" />
                        </span>
                        <p className="ml-1 my-0 py-1 text-nowrap text-muted">Add HOA</p>
                      </div>
                    </div>
                  ) : null
                }
              />
              <div className="px-2 my-2">
                {show && /^hoa[1-3]$/.test(formSteps[step]?.key || '') && (() => {
                  const hoaIndex =
                    Number(String(formSteps[step]?.key || '').replace('hoa', '')) - 1;
                  const hoaSlot = orderHoaDisplay[hoaIndex] || {};
                  return (
                    <EstoppelReportHoaSummaryPanel
                      hoaSlot={hoaSlot}
                      canEdit={canEditHoa}
                      disabled={!!loading}
                      canEditExplanation={canEditHoa}
                      onEdit={() => {
                        setUpdateHoaEditIndex(hoaIndex);
                        setShowUpdateHoaModal(true);
                      }}
                      showExplanationField={!!hoaSlot.noEstoppelNeeded}
                      explanation={localNoEstoppelExplanations[hoaIndex] ?? ''}
                      onExplanationChange={value => {
                        setLocalNoEstoppelExplanations(prev => {
                          const next = [...prev];
                          while (next.length <= hoaIndex) next.push('');
                          next[hoaIndex] = value;
                          return next;
                        });
                      }}
                    />
                  );
                })()}
              </div>
              <div className="p-2 px-4">
                {show && formSteps[step] && activeStepFields.length > 0 && (
                  <ReportFormGenerator
                    orderType={ORDER_TYPES.ESTOPPEL}
                    defaultValues={defaultValues}
                    field={formSteps[step].key}
                    fields={activeStepFields}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    service={service}
                    isFilePopupOpen={isLayoutConstrained}
                    driveFiles={driveFiles}
                    driveFilesLoading={driveFilesLoading}
                    onUpdateDriveFiles={setDriveFiles}
                    setConfirmationModal={setConfirmationModal}
                    showValidationErrors={showValidationErrors}
                    setValidationStatus={setShowValidationErrors}
                    enableFieldStatus={hasQaFieldStatuses || Object.keys(validationFieldStatuses).length > 0}
                    fieldStatuses={mergedFieldStatuses}
                    onMultiFieldChange={handleInvoiceMultiFieldChange}
                    handleRefresh={() => {
                      if (service?.driveFolder) loadDriveFiles(service.driveFolder);
                    }}
                    onFieldValueChange={({ field, key, value, formValues: fv, setFormValues: setFv }) => {
                      if (key === 'invoicesFiles') {
                        autoFillInvoicesFromFiles({
                          reportPropertyAddress: fileInformation.propertyAddress,
                          value,
                          formValues: fv,
                          setFormValues: setFv,
                          reportType: "Estoppel"
                        });
                        return true;
                      }

                      if (
                        ['hoa1', 'hoa2', 'hoa3'].includes(field) &&
                        key === 'paidThroughChoice'
                      ) {
                        const newFormValues = { ...fv };
                        if (value === PAID_THROUGH_TEXT_OPTION) {
                          newFormValues[field] = {
                            ...(newFormValues[field] || {}),
                            paidThroughChoice: value,
                            paidThrough: ''
                          };
                        } else {
                          newFormValues[field] = {
                            ...(newFormValues[field] || {}),
                            paidThroughChoice: value,
                            paidThroughText: ''
                          };
                        }
                        setFv(newFormValues);
                        return true;
                      }

                      if (
                        field === 'invoices' &&
                        ['hardCost', 'estoppelProcessingFee', 'minimumInvoicableAmount'].includes(key)
                      ) {
                        const newFormValues = { ...fv };
                        if (!newFormValues.invoices) newFormValues.invoices = {};
                        newFormValues.invoices[key] = value;

                        const currentInvoices = newFormValues.invoices;
                        const hardCost = Number(currentInvoices.hardCost) || 0;
                        const processingFee = Number(currentInvoices.estoppelProcessingFee) || 0;
                        const minimumInvoicableAmount = Number(currentInvoices.minimumInvoicableAmount) || 0;

                        currentInvoices.suggestedInvoiceAmount = computeSuggestedInvoiceAmount(
                          hardCost,
                          processingFee,
                          minimumInvoicableAmount
                        );

                        setFv(newFormValues);
                        return true;
                      }

                      return false;
                    }}
                  />
                )}
              </div>
              </div>
              {qaAnalysisMeta?.show && (
                <QaAnalysisSideBar
                  orderId={reportOrderId}
                  orderType={ORDER_TYPES.ESTOPPEL}
                  qaDocumentId={qaDocumentId}
                  onQaDocumentIdChange={setQaDocumentId}
                  sections={qaSections}
                  onClose={closeQaPanel}
                  onIssueClick={handleQaIssueClick}
                  onIssuesChange={handleQaIssuesChange}
                  activeIssueId={activeQaIssueId}
                  hasUnsavedChanges={hasUnsavedChanges}
                  onSaveReport={onSaveReport}
                  isSavingReport={isSaving}
                  analyzedAt={qaAnalyzedAt}
                />
              )}
            </div>

            <ReportActionBar
              onPreviewHistory={onPreviewHistory}
              onSaveReport={onSaveReport}
              onGenerateReport={onGenerateReport}
              onQuickPreview={onQuickPreview}
              isGeneratingReport={isGeneratingReport}
              isSaving={isSaving}
              reportGenerationMode={reportGenerationMode}
            />

            <ReportSidebarFooter
              historyReport={historyReport}
              showHistory={showHistory}
              onCloseHistory={onCloseHistory}
              handleHideGeneratedFile={handleHideGeneratedFile}
              fileShareMetadata={fileShareMetadata}
              setFileShareMetadata={setFileShareMetadata}
              service={service}
              report={estoppel?.report}
              reportId={estoppel?.report?._id}
              onHistoryReportChange={onHistoryReportChange}
              shareEndpoint={ENDPOINTS.SHARE_ESTOPPEL_REPORT}
              saveEndpoint={ENDPOINTS.ESTOPPEL_REPORT_WITH_ID}
              generatedFile={generatedFile}
              reportGenerationMode={reportGenerationMode}
              isGeneratingReport={isGeneratingReport}
              onFileShareClick={onFileShareClick}
              onRefreshFile={onRefreshFile}
              onShareComplete={onAfterSave}
            />
          </>
        )}

        <ReportModals
          confirmationModal={confirmationModal}
          setConfirmationModal={setConfirmationModal}
          showUnsavedChangesAlert={showUnsavedChangesAlert}
          handleCancelAction={handleCancelAction}
          handleDiscardChanges={handleDiscardChanges}
          handleSaveAndContinue={handleSaveAndContinue}
          isSaving={isSaving}
        />
      </SlidingSideBar>

      <AddEditOrderSidebar
        orderType="Estoppel"
        show={showUpdateHoaModal}
        hoaEditIndex={updateHoaEditIndex}
        enablePreferredContactsForHoaEdit
        onHide={() => {
          setShowUpdateHoaModal(false);
          setUpdateHoaEditIndex(null);
        }}
        editingOrder={editingEstoppelOrder}
        service={service}
        onOrderAddUpdate={(savedOrder, { close } = {}) => {
          if (!Array.isArray(savedOrder?.homeOwnerInformation)) return;
          const slots = padHoaDisplaySlots(savedOrder.homeOwnerInformation, hoaSlotCount);
          setOrderHoaDisplayOverride(savedOrder.homeOwnerInformation);
          setInitialFormState(prev =>
            prev
              ? {
                  ...prev,
                  hoaDirectory: normalizeHoaDirectorySnapshot(slots, hoaSlotCount)
                }
              : prev
          );
          onOrderHoaUpdated?.(savedOrder);
          if (close !== false) setUpdateHoaEditIndex(null);
        }}
      />
    </>
  );
};

export default AddEditEstoppelReportSidebar;
